import { collection, doc, setDoc, writeBatch } from 'firebase/firestore';
import { IComparisonPopulationSetting } from '../../../providers/settings/comparisonPopulationSettingsProvider';
import { IDateRange } from '../../../providers/settings/dates/dateRangeProvider';
import { IQuadrantReportSettings } from '../../../providers/settings/reports/quadrantReportSettingsProvider';
import { ISettingsTemplate } from '../../../providers/settings/reports/types';
import { userAsPromise } from '../../../providers/userProvider';

/**
 * @description saves the template as the selected template and additional settings
 * @param template
 */
export const saveQuadrantSettings = async (
  template: ISettingsTemplate<IQuadrantReportSettings>,
  timeframe: IDateRange,
  comparisonPopulation: IComparisonPopulationSetting
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/quadrantReport`
  );

  await setDoc(
    docRef,
    {
      selected: template ?? null,
      timeframe: timeframe ?? null,
      comparisonPopulation,
    },
    { merge: true }
  );
};

/**
 * @description updates the quadrant template
 * @param template
 * @param isSelected
 */
export const updateQuadrantTemplate = async (
  template: ISettingsTemplate<IQuadrantReportSettings>,
  isSelected: boolean
): Promise<void> => {
  const user = await userAsPromise();

  if (isSelected) {
    const batch = writeBatch(user.services.firestore);
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/quadrantReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
    batch.set(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/quadrantReport`
      ),
      { selected: template },
      { merge: true }
    );
    await batch.commit();
  } else {
    await setDoc(
      doc(
        user.services.firestore,
        `userProfiles/${user.id}/settings/quadrantReport/templates/${template.id}`
      ),
      template,
      { merge: true }
    );
  }
};

/**
 * @description delete the template and change the current template to another one
 * @param template
 * @param replacement
 * @returns
 */
export const deleteSelectedTemplate = async (
  template: ISettingsTemplate<IQuadrantReportSettings>,
  replacement: ISettingsTemplate<IQuadrantReportSettings>
): Promise<void> => {
  const user = await userAsPromise();

  const batch = writeBatch(user.services.firestore);
  const updateRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/quadrantReport/templates/${template.id}`
  );
  const update: ISettingsTemplate<IQuadrantReportSettings> = {
    ...template,
    active: false,
  };
  batch.update(updateRef, update as unknown as Record<string, unknown>);
  const selectedRef = doc(
    user.services.firestore,
    `userProfiles/${user.id}/settings/quadrantReport`
  );
  batch.set(selectedRef, { selected: replacement ?? null }, { merge: true });
  return batch.commit();
};

/**
 * @description creates a new quadrant report settings template
 * @param template
 */
export const createQuadrantTemplate = async (
  template: Partial<ISettingsTemplate<IQuadrantReportSettings>>
): Promise<void> => {
  const user = await userAsPromise();

  const docRef = doc(
    collection(
      user.services.firestore,
      `userProfiles/${user.id}/settings/quadrantReport/templates`
    )
  );
  const newDocument = {
    ...template,
    id: docRef.id,
    active: true,
  };
  await setDoc(docRef, newDocument, { merge: true });
};
